import type { EntityViewLevel, LayerModel } from '../core/types';

/** Search result over logical entities (never physical fragments). */
export interface EntitySearchResult {
  readonly kind: Exclude<EntityViewLevel, 'fragment'>;
  readonly id: string;
  readonly label: string;
  readonly code: string;
  readonly context: string;
  readonly score: number;
  /** Why it matched, e.g. "Finca 993 + Suerte 25" for a combined code. */
  readonly reason?: string;
}

export const normalizeSearchText = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('es').replace(/\s+/g, ' ').trim();
const compact = (value: string): string => normalizeSearchText(value).replace(/[\s._/-]+/g, '');
/** "025" ≡ "25" and "14a" ≡ "14A"; text codes compare literally. */
const sameCode = (left: string, right: string): boolean => {
  const a = compact(left); const b = compact(right);
  if (!a || !b) return false;
  if (a === b) return true;
  return /^\d+$/.test(a) && /^\d+$/.test(b) && Number(a) === Number(b);
};

interface IndexedFinca { readonly id: string; readonly name: string; readonly code: string; readonly nameKey: string; readonly codeKey: string; readonly words: readonly string[] }
interface IndexedSuerte { readonly id: string; readonly fincaId: string; readonly label: string; readonly code: string }
interface IndexedTablon { readonly id: string; readonly suerteId: string; readonly code: string }
interface SearchIndex { readonly fincas: readonly IndexedFinca[]; readonly fincasById: ReadonlyMap<string, IndexedFinca>; readonly suertes: readonly IndexedSuerte[]; readonly suertesByFinca: ReadonlyMap<string, readonly IndexedSuerte[]>; readonly tablonesBySuerte: ReadonlyMap<string, readonly IndexedTablon[]> }

const indexCache = new WeakMap<LayerModel, SearchIndex>();
function indexFor(model: LayerModel): SearchIndex {
  const cached = indexCache.get(model); if (cached) return cached;
  const fincas = [...model.fincas.values()].map((finca) => ({ id: finca.id, name: finca.name, code: finca.code, nameKey: normalizeSearchText(finca.name), codeKey: compact(finca.code), words: normalizeSearchText(finca.name).split(' ') }));
  const suertes = [...model.suertes.values()].map((suerte) => ({ id: suerte.id, fincaId: suerte.fincaId, label: suerte.label, code: suerte.code }));
  const suertesByFinca = new Map<string, IndexedSuerte[]>(); suertes.forEach((suerte) => suertesByFinca.set(suerte.fincaId, [...(suertesByFinca.get(suerte.fincaId) ?? []), suerte]));
  const tablonesBySuerte = new Map<string, IndexedTablon[]>(); model.tablones.forEach((tablon) => tablonesBySuerte.set(tablon.suerteId, [...(tablonesBySuerte.get(tablon.suerteId) ?? []), { id: tablon.id, suerteId: tablon.suerteId, code: tablon.code }]));
  const index = { fincas, fincasById: new Map(fincas.map((finca) => [finca.id, finca])), suertes, suertesByFinca, tablonesBySuerte };
  indexCache.set(model, index);
  return index;
}

/** Name relevance: exact > prefix > word prefix > substring (≥3 chars). No fuzzy edit-distance matching. */
const nameScore = (finca: IndexedFinca, query: string): number => {
  if (!query) return 0;
  if (finca.nameKey === query) return 100;
  if (finca.nameKey.startsWith(query)) return 86;
  if (finca.words.some((word) => word.startsWith(query))) return 74;
  if (query.length >= 3 && finca.nameKey.includes(query)) return 58;
  return 0;
};

/**
 * Searches Fincas by name/code, Suertes by number/code, and interpretable combinations:
 * "99325" → Finca 993 + Suerte 25, "Pansaco 25", "993-25", "993 25 3" (Finca + Suerte + Tablón).
 */
export function searchEntities(model: LayerModel | null, rawQuery: string, limit = 12): readonly EntitySearchResult[] {
  if (!model) return [];
  const query = normalizeSearchText(rawQuery.replace(/^(finca|hacienda)\s+/i, ''));
  if (!query) return [];
  const index = indexFor(model);
  const results = new Map<string, EntitySearchResult>();
  const add = (result: EntitySearchResult): void => { const current = results.get(result.id); if (!current || current.score < result.score) results.set(result.id, result); };
  const fincaResult = (finca: IndexedFinca, score: number, reason?: string): EntitySearchResult => ({ kind: 'finca', id: finca.id, label: finca.name, code: finca.code, context: `Finca · Código ${finca.code}`, score, reason });
  const suerteResult = (suerte: IndexedSuerte, score: number, reason?: string): EntitySearchResult => {
    const finca = index.fincasById.get(suerte.fincaId);
    return { kind: 'suerte', id: suerte.id, label: `Suerte ${suerte.label}`, code: suerte.code, context: `Suerte · ${finca?.name ?? ''} (${finca?.code ?? ''})`, score, reason };
  };
  const tablonResult = (tablon: IndexedTablon, suerte: IndexedSuerte, score: number, reason?: string): EntitySearchResult => {
    const finca = index.fincasById.get(suerte.fincaId);
    return { kind: 'tablon', id: tablon.id, label: `Tablón ${tablon.code}`, code: tablon.code, context: `Tablón · ${finca?.name ?? ''} › Suerte ${suerte.label}`, score, reason };
  };
  const strippedSuerte = query.replace(/^suerte\s+/, '');
  const queryCode = compact(query);

  // 1. Fincas by name or code.
  index.fincas.forEach((finca) => {
    const byCode = sameCode(finca.code, query) ? 98 : queryCode.length >= 2 && finca.codeKey.startsWith(queryCode) && /^\d+$/.test(queryCode) ? 62 : 0;
    const score = Math.max(nameScore(finca, query), byCode);
    if (score) add(fincaResult(finca, score));
  });

  // 2. Suertes by their own code (e.g. "99325" when CodSuerte already holds it) or by number ("14A", "suerte 14").
  index.suertes.forEach((suerte) => {
    if (sameCode(suerte.code, query) && compact(suerte.code) !== compact(suerte.label)) add(suerteResult(suerte, 96, `Código de Suerte ${suerte.code}`));
    else if (/^suerte\s+/.test(query) && sameCode(suerte.label, strippedSuerte)) add(suerteResult(suerte, 66));
    else if (/[a-z]/.test(queryCode) && /\d/.test(queryCode) && sameCode(suerte.label, query)) add(suerteResult(suerte, 72));
  });

  // 3. Combined numeric code without separators: Finca code prefix + Suerte number ("99325" → 993 + 25).
  //    An explicit separator ("993-25") is unambiguous and handled by the token rule below.
  if (/^[0-9a-z]+$/.test(query) && /^\d/.test(query)) {
    index.fincas.forEach((finca) => {
      if (!finca.codeKey || !queryCode.startsWith(finca.codeKey) || queryCode.length === finca.codeKey.length) return;
      const rest = queryCode.slice(finca.codeKey.length);
      (index.suertesByFinca.get(finca.id) ?? []).forEach((suerte) => {
        if (sameCode(suerte.label, rest) || sameCode(suerte.code, rest)) add(suerteResult(suerte, 95, `Finca ${finca.code} + Suerte ${suerte.label}`));
      });
    });
  }

  // 4. Tokenised combinations: "<finca name or code> <suerte> [<tablón>]".
  const tokens = query.split(/[\s/-]+/).filter(Boolean);
  if (tokens.length >= 2) {
    const tryCombination = (fincaPart: string, suertePart: string, tablonPart?: string): void => {
      index.fincas.forEach((finca) => {
        const fincaMatch = sameCode(finca.code, fincaPart) ? 1 : nameScore(finca, fincaPart) >= 74 ? 0.9 : 0;
        if (!fincaMatch) return;
        (index.suertesByFinca.get(finca.id) ?? []).forEach((suerte) => {
          if (!sameCode(suerte.label, suertePart) && !sameCode(suerte.code, suertePart)) return;
          if (!tablonPart) { add(suerteResult(suerte, 90 * fincaMatch + 4, `${finca.name} + Suerte ${suerte.label}`)); return; }
          (index.tablonesBySuerte.get(suerte.id) ?? []).forEach((tablon) => { if (sameCode(tablon.code, tablonPart)) add(tablonResult(tablon, suerte, 88 * fincaMatch + 4, `${finca.name} › Suerte ${suerte.label} › Tablón ${tablon.code}`)); });
        });
      });
    };
    const last = tokens.at(-1)!;
    tryCombination(tokens.slice(0, -1).join(' '), last);
    if (tokens.length >= 3) tryCombination(tokens.slice(0, -2).join(' '), tokens.at(-2)!, last);
  }

  const order: Record<EntitySearchResult['kind'], number> = { finca: 0, suerte: 1, tablon: 2 };
  return [...results.values()]
    .sort((a, b) => b.score - a.score || order[a.kind] - order[b.kind] || a.context.localeCompare(b.context, 'es', { numeric: true }) || a.label.localeCompare(b.label, 'es', { numeric: true }))
    .slice(0, limit);
}
